import React, {useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { AppBar, Toolbar, Typography, IconButton, Menu, MenuItem } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import LogOutIcon from '@material-ui/icons/ExitToApp';
import PersonIcon from '@material-ui/icons/Person';
import { onLogout } from 'utils/settings';
import useRouter from 'utils/useRouter';
import { setLogoutClick } from 'actions';
import ChangePassword from 'components/ChangePassword/ChangePassword';

const useStyles = makeStyles(theme => ({
    applicantCenterTitle: {
        marginLeft: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            marginLeft: 0,
        }
     },
    companyName: {
        color: theme.palette.white,
    },
    container: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            flexDirection: 'column',
        },
        '&.maxLengthCompanyName': {
            [theme.breakpoints.down('md')]: {
                zoom: '0.8',
                overflowWrap: 'anywhere',
            },
        }
    },
    heading: {
        color: theme.palette.white,
        fontWeight: '400',
        lineHeight: 'normal',
    },
    headerWrapper: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    },
    imgContainter: {
        display: 'block',
    },
    logoImg: {
        maxHeight: '80px',
        [theme.breakpoints.down('xs')]: {
            maxWidth: '130px',
        }
    },
    menuButton: {
        marginLeft: 'auto',
        color: theme.palette.white,
    },
    menuItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    menuIcon: {
        marginRight: theme.spacing(1),
    },
    root: {
        boxShadow: 'none',
        backgroundColor: theme.palette.darkBlue
    },
    title: {
        width: '100%',
        textAlign: 'center',
    },
}));

const Topbar = props => {
    const { className, ...rest } = props;
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const router = useRouter();
    const dispatch = useDispatch();

    const jobDetails = useSelector(state => state.auth.jobDetails);
    const isLogoutClick = useSelector(state => state.wizard?.isLogoutClick);

    // Check if current path contains "/application" to hide menu on non-authenticated pages
    const isApplicationPath = router.location.pathname.includes('/application');
    const isLandingPage = router.location.pathname.includes('/applicantLandingPage');

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        handleMenuClose();
        dispatch(setLogoutClick(true));
        if (isLandingPage) {
            window.onbeforeunload = null;
            onLogout();
        }
    };

    const handleChangePassword = () => {
        handleMenuClose();
        setIsChangePasswordOpen(true);
    };

    return (
        <>
        <AppBar {...rest} className={clsx(classes.root, className)}>
            <Toolbar className={classes.headerWrapper}>
                { (jobDetails.logoInBase64Format || jobDetails.companyName)
                    ? <div className={[classes.container, jobDetails.companyName?.length > 50 && 'maxLengthCompanyName']?.filter(f => f)?.join(' ')}>
                        { jobDetails?.logoInBase64Format 
                        ? <div className={classes.imgContainter}>
                            <img 
                                alt="Logo" 
                                src={ jobDetails?.logoInBase64Format?.startsWith("data:image") 
                                        ? jobDetails?.logoInBase64Format 
                                        : 'data:image/jpeg;base64,' + jobDetails?.logoInBase64Format
                                } 
                                className={classes.logoImg} 
                                />
                        </div>
                        : <Typography className={classes.companyName} variant="h3">
                            {jobDetails.companyName}
                        </Typography> }
                        <Typography className={clsx(classes.heading, classes.applicantCenterTitle)} variant="h3">
                            | Applicant Center
                        </Typography>
                        </div>
                    : <Typography className={clsx(classes.heading, classes.title)} variant="h3">
                        Applicant Center
                    </Typography>
                }
                {!isApplicationPath && (
                    <>
                        <IconButton
                            className={classes.menuButton}
                            onClick={handleMenuOpen}
                            aria-label="menu"
                            aria-controls="user-menu"
                            aria-haspopup="true"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="user-menu"
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            keepMounted
                            getContentAnchorEl={null}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem onClick={handleChangePassword} className={classes.menuItem}>
                                <PersonIcon className={classes.menuIcon} />
                                Change Password
                            </MenuItem>
                            <MenuItem onClick={handleLogout} className={classes.menuItem}>
                                <LogOutIcon className={classes.menuIcon} />
                                Logout
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Toolbar>
        </AppBar>
        <ChangePassword
            isOpen={isChangePasswordOpen}
            onClose={() => setIsChangePasswordOpen(false)}
        />
        </>
    );
};

Topbar.propTypes = {
    className: PropTypes.string
};

export default Topbar;